import React, { useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm, useFormState } from 'react-hook-form';
import * as yup from 'yup';
import { always } from 'ramda';

import { Block, BlockContent, BlockCopy, PointTitle } from '@components/PDP/pdp.styles';
import { BUTTON_SIZE, Error, formMessages } from '@components/form';
import { useYupValidationResolver } from '@hooks/useYup';
import { useCheckBalance } from '@hooks/useGiftCertificate';
import reportError from '@utils/reportError';
import { renderWhenTrue, renderWhenTrueOtherwise } from '@utils/rendering';

import { Button, TextField, Balance } from './giftCertificateBalance.styles';

const CODE = 'code';

interface Form {
  [CODE]: string;
}

export const GiftCertificateBalance = () => {
  const intl = useIntl();
  const { checkBalance, giftCertificate } = useCheckBalance();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const validationSchema = useMemo(
    () =>
      yup.object({
        [CODE]: yup.string().required(intl.formatMessage(formMessages.required)),
      }),
    []
  );

  const resolver = useYupValidationResolver(validationSchema);
  const { register, control, handleSubmit } = useForm<Form>({
    defaultValues: {
      [CODE]: '',
    },
    resolver,
  });
  const { errors } = useFormState({ control });

  const handleCheckBalance = async (data: Form) => {
    try {
      setLoading(true);
      setError(false);
      await checkBalance(data.code);
    } catch (e) {
      setError(true);
      reportError(e);
    } finally {
      setLoading(false);
    }
  };

  const renderError = renderWhenTrue(
    always(
      <Error>
        <FormattedMessage {...formMessages.generalError} />
      </Error>
    )
  );

  const renderForm = renderWhenTrueOtherwise(
    always(
      <form onSubmit={handleSubmit(handleCheckBalance)}>
        <TextField
          register={register(CODE)}
          error={errors[CODE]}
          placeholder={intl.formatMessage({ id: 'giftCertificateBalance.input', defaultMessage: 'Code:' })}
        />
        {renderError(error)}
        <Button fullWidth size={BUTTON_SIZE.XXL} type="submit" disabled={loading}>
          <FormattedMessage id="giftCertificateBalance.submit" defaultMessage="CHECK" />
        </Button>
      </form>
    ),
    always(
      <Balance>
        <FormattedMessage
          id="giftCertificateBalance.balance"
          defaultMessage="Your balance is ${balance}."
          values={{ balance: giftCertificate.balance }}
        />
      </Balance>
    )
  );

  return (
    <Block>
      <PointTitle upperCase>
        <FormattedMessage id="giftCertificateBalance.title" defaultMessage="Check Balance:" />
      </PointTitle>
      <BlockContent>
        <BlockCopy>
          <FormattedMessage id="giftCertificateBalance.question" defaultMessage="Already have an eGift Card?" />
        </BlockCopy>
        <BlockCopy>
          <FormattedMessage id="giftCertificateBalance.check" defaultMessage="Check the balance on your card below." />
        </BlockCopy>
        {renderForm(!giftCertificate.balance)}
      </BlockContent>
    </Block>
  );
};
